import DeleteIcon from "@mui/icons-material/Delete";
import FormHelperText from "@mui/material/FormHelperText";
import { Controller } from "react-hook-form";

import { Box } from "@/Components/v2/atoms/Box";
import { IconButton } from "@/Components/v2/atoms/Button";
import { FormControl } from "@/Components/v2/atoms/FormControl";
import { MenuItem } from "@/Components/v2/atoms/MenuItem";
import { Select } from "@/Components/v2/atoms/Select";
import { DollarTextField } from "@/Components/v2/atoms/TextField";

import { StyledTableRow } from "../../components/StyledComponents";
import { useScenarioForms } from "../scenario-form/ScenarioFormProvider";
import { ForecastConstraintRowProps, OptimizerConstraintRowProps } from "./types";

export const OptimizerConstraintRow: React.FC<OptimizerConstraintRowProps> = ({
  constraint,
  globalIndex,
  availableChannels,
  disabled = false,
  currencySymbol,
  helperText,
}) => {
  const { mediaConstraintsForm, handleMediaConstraintsRowChange } = useScenarioForms();

  const handleChannelChange = (value: string) => {
    handleMediaConstraintsRowChange({
      type: "update",
      index: globalIndex,
      value: { ...constraint, channel: value },
    });
  };

  const handleMinValueChange = (value: number | undefined) => {
    handleMediaConstraintsRowChange({
      index: globalIndex,
      type: "update",
      value: { ...constraint, minValue: value ?? 0 },
    });
  };

  const handleMaxValueChange = (value: number | undefined) => {
    handleMediaConstraintsRowChange({
      index: globalIndex,
      type: "update",
      value: { ...constraint, maxValue: value ?? 0 },
    });
  };

  const handleRemove = () => {
    handleMediaConstraintsRowChange({ index: globalIndex, type: "remove" });
  };

  return (
    <StyledTableRow>
      <Box sx={{ display: "flex", flex: 1, pr: 1, pt: 1 }}>
        <Box flex="1" pr={1}>
          <Controller
            name={`mediaConstraints.${globalIndex}.channel`}
            control={mediaConstraintsForm.control}
            render={({ field, fieldState }) => (
              <FormControl fullWidth size="small">
                <Select
                  {...field}
                  variant="standard"
                  disabled={disabled}
                  size="small"
                  error={fieldState.error ? true : false}
                  onChange={(event) => handleChannelChange(event.target.value)}
                >
                  {availableChannels.map((option) => (
                    <MenuItem key={option.key} value={option.key} disabled={option.disabled}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {fieldState.error && (
                  <FormHelperText error>{fieldState.error.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Box>

        <Box flex="1" px={0.5}>
          <Controller
            name={`mediaConstraints.${globalIndex}.minValue`}
            control={mediaConstraintsForm.control}
            render={({ field, fieldState }) => (
              <DollarTextField
                {...field}
                id={`mediaConstraints.${globalIndex}.minValue`}
                placeholder={helperText.minValue}
                fullWidth
                size="small"
                variant="standard"
                allowNegative={false}
                disabled={disabled || !constraint.channel}
                currencySymbol={currencySymbol}
                error={fieldState.error ? true : false}
                helperText={
                  mediaConstraintsForm.formState.errors.mediaConstraints?.[globalIndex]?.minValue
                    ?.message
                }
                onChange={handleMinValueChange}
              />
            )}
          />
        </Box>

        <Box flex="1" px={0.5}>
          <Controller
            name={`mediaConstraints.${globalIndex}.maxValue`}
            control={mediaConstraintsForm.control}
            render={({ field, fieldState }) => (
              <DollarTextField
                {...field}
                id={`mediaConstraints.${globalIndex}.maxValue`}
                placeholder={helperText.maxValue}
                fullWidth
                size="small"
                variant="standard"
                allowNegative={false}
                disabled={disabled || !constraint.channel}
                helperText={
                  mediaConstraintsForm.formState.errors.mediaConstraints?.[globalIndex]?.maxValue
                    ?.message
                }
                error={fieldState.error ? true : false}
                currencySymbol={currencySymbol}
                onChange={handleMaxValueChange}
              />
            )}
          />
        </Box>

        <Box width="48px" display="center" justifyContent="center">
          {!disabled && (
            <IconButton
              size="small"
              onClick={handleRemove}
              disabled={disabled}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "error.main",
                  backgroundColor: "error.light",
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
    </StyledTableRow>
  );
};

export const ForecastConstraintRow: React.FC<ForecastConstraintRowProps> = ({
  constraint,
  globalIndex,
  availableChannels,
  disabled = false,
  currencySymbol,
  helperText,
  onRemove,
  form,
}) => {
  const { handleSpendInputRowChange } = useScenarioForms();

  const handleChannelChange = (value: string) => {
    handleSpendInputRowChange({
      type: "update",
      index: globalIndex,
      value: { ...constraint, channel: value },
    });
  };

  const handleSpendChange = (value: number | undefined) => {
    handleSpendInputRowChange({
      index: globalIndex,
      type: "update",
      value: { ...constraint, spend: value ?? 0 },
    });
  };

  return (
    <StyledTableRow>
      <Box sx={{ display: "flex", flex: 1, pr: 1, pt: 1 }}>
        <Box flex="1" pr={1}>
          <Controller
            name={`spendInputs.${globalIndex}.channel`}
            control={form.control}
            render={({ field, fieldState }) => (
              <FormControl fullWidth size="small">
                <Select
                  {...field}
                  onChange={(event) => handleChannelChange(event.target.value)}
                  variant="standard"
                  disabled={disabled}
                  size="small"
                  error={fieldState.error ? true : false}
                >
                  {availableChannels.map((option) => (
                    <MenuItem key={option.key} value={option.key} disabled={option.disabled}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {fieldState.error && (
                  <FormHelperText error>{fieldState.error.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Box>

        <Box flex="1" px={0.5}>
          <Controller
            name={`spendInputs.${globalIndex}.spend`}
            control={form.control}
            render={({ field, fieldState }) => (
              <DollarTextField
                value={field.value}
                onChange={handleSpendChange}
                onBlur={field.onBlur}
                name={field.name}
                placeholder={helperText.maxValue}
                fullWidth
                size="small"
                variant="standard"
                allowNegative={false}
                disabled={disabled || !constraint.channel}
                helperText={form.formState.errors.spendInputs?.[globalIndex]?.spend?.message}
                error={Boolean(fieldState.error)}
                currencySymbol={currencySymbol}
              />
            )}
          />
        </Box>

        <Box width="48px" display="center" justifyContent="center">
          {!disabled && (
            <IconButton
              size="small"
              onClick={onRemove}
              disabled={disabled}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "error.main",
                  backgroundColor: "error.light",
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
    </StyledTableRow>
  );
};
